import React, { type HTMLAttributes, useRef, useState } from 'react';
import type { RuleSet } from 'styled-components';
import { css } from 'styled-components';
import { IconCamera } from '@/app/common/icons/libraryIcons';
import { Picture } from '@/app/components/hotel-detail/ImageWithFallback';
import HeroGallerySliderOverlay from '@/app/components/hotel-detail/HeroGallerySliderOverlay';
import { useImagePreload } from '@/app/components/hotel-detail/hooks/useImagePreload';
import {
  getImageSizes,
  getImageSrc,
  getImageSrcSet,
  getSingleSource,
  getSourceMobile,
  type ResponsiveOptions,
  transformationDesktopRoomPanelLayout,
  transformationMobileRoomPanelLayout,
} from '@/app/components/hotel-detail/getGallerySourceOptimization';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useIsMobile } from './hooks/useMediaSSR';

type ImageSliderProps = {
  images?: readonly string[];
  roomName?: string;
  disableCount?: boolean;
  title?: string;
  imageIndex?: number;
  fullScreen?: boolean;
  showCounterIcon?: boolean;
  counterPosition?: RuleSet<object> | string;
  onImageClickHandler?: (index: number) => void;
} & React.HTMLAttributes<HTMLElement>;

type ImageSliderFullScreenProps = {
  animation?: 'scroll' | 'fade' | 'none';
  images: readonly string[];
  imageIndex: number;
  onSlideChange?: (index: number) => void;
  transformations?: {
    url: (url: string) => string;
    sources: (url: string) => string;
    sizes?: (url: string) => string | undefined;
  };
  counterDisplay?: 'none' | 'default' | 'hero';
} & React.HTMLAttributes<HTMLElement>;

type ImageSliderContainerProps = {
  children: React.ReactNode;
  containerRef: React.RefObject<HTMLDivElement | null>;
  scrollable?: boolean;
} & React.HTMLAttributes<HTMLElement>;

function ImageSliderContainer(props: ImageSliderContainerProps) {
  const { children, containerRef, scrollable = true, ...rest } = props;

  return (
    <div
      ref={containerRef}
      className="slider-container"
      css={css`
        height: 100%;
        display: flex;
        justify-content: space-between;
        overflow-x: ${scrollable ? 'auto' : 'hidden'};
        overflow-y: ${scrollable ? 'hidden' : 'visible'};
        scroll-snap-type: ${scrollable ? 'x mandatory' : 'none'};
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        position: relative;

        &::scrollbar {
          display: none;
        }

        &::-webkit-scrollbar {
          display: none;
        }
      `}
      {...rest}
    >
      {children}
    </div>
  );
}

type ReallyLazyImageSlideProps = {
  url: string;
  sources?: { srcSet: string; media?: string; sizes?: string }[];
  numberOfImages: number;
  currentIndex: number;
  index: number;
  onImageClick?: () => void;
  fullScreen?: boolean;
} & HTMLAttributes<HTMLDivElement>;

function ReallyLazyImageSlide(props: ReallyLazyImageSlideProps) {
  const { index, numberOfImages, currentIndex, url, sources, onImageClick, fullScreen } = props;
  const [shouldRender] = useImagePreload({ currentIndex, index, numberOfImages });
  return shouldRender ? (
    <ImageSlide url={url} sources={sources} loading={shouldRender ? 'eager' : 'lazy'} onClick={onImageClick} />
  ) : fullScreen ? (
    <IconCamera
      css={css`
        color: ${(p) => p.theme.commons.colors.gray500};
        width: 20%;
        height: 20%;
        top: 40%;
        left: 40%;
        position: fixed;
        z-index: -1;
      `}
    />
  ) : (
    <div
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
      `}
    >
      <IconCamera
        css={css`
          color: ${(p) => p.theme.commons.colors.gray500};
          width: 40%;
          height: 40%;
          position: relative;
          z-index: -1;
        `}
      />
    </div>
  );
}

export default function ImageSlider(props: ImageSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    images,
    // title,
    disableCount,
    imageIndex,
    showCounterIcon = true,
    counterPosition,
    onImageClickHandler = () => {},
    ...rest
  } = props;
  const [currentIndex, setCurrentIndex] = useState(0);
  const isMobile = useIsMobile();
  const counterDisplay = disableCount ? 'none' : 'default';

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {images && (
        <div
          {...rest}
          css={css`
            position: relative;
            height: 100%;
          `}
        >
          <ImageSliderContainer containerRef={containerRef}>
            {images.map((image, index) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
              <div
                key={image}
                css={`
                  flex: 0 0 100%;
                  scroll-snap-align: center;
                  scroll-snap-stop: always;
                `}
                onClick={() => onImageClickHandler(index)}
              >
                <ReallyLazyImageSlide
                  url={
                    image.includes('bstatic.com')
                      ? image.replace('/max500/', '/max1000/')
                      : getSingleSource(
                          image,
                          isMobile ? transformationMobileRoomPanelLayout : transformationDesktopRoomPanelLayout
                        )
                  }
                  index={index}
                  numberOfImages={images ? images.length : 0}
                  currentIndex={currentIndex}
                />
              </div>
            ))}
          </ImageSliderContainer>
          {
            <HeroGallerySliderOverlay
              initialImageIndex={imageIndex}
              scrollContainerRef={containerRef}
              amountOfSlides={images ? images.length : 0}
              slideChanged={setCurrentIndex}
              counterDisplay={counterDisplay}
              showCounterIcon={showCounterIcon}
              counterPosition={counterPosition}
            />
          }
        </div>
      )}
    </>
  );
}

export function ImageSliderFullScreen(props: ImageSliderFullScreenProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    animation = 'scroll',
    images,
    imageIndex,
    transformations,
    onSlideChange,
    counterDisplay = 'default',
    ...rest
  } = props;
  const [currentIndex, setCurrentIndex] = useState(imageIndex);

  const onChange = (index: number) => {
    setCurrentIndex(index);
    onSlideChange?.(index);
  };

  return images ? (
    <div
      css={`
        position: relative;
      `}
      {...rest}
    >
      <ImageSliderContainer containerRef={containerRef} scrollable={animation === 'scroll'}>
        {images.map((image, index) => (
          <div
            key={image}
            css={`
              flex: 0 0 100%;
              scroll-snap-align: center;
              scroll-snap-stop: always;
              ${index === currentIndex || animation === 'scroll' || animation === 'none' ? 'opacity:1;' : 'opacity:0;'}
              transition: opacity 0.1s ease-in-out;
            `}
          >
            <ReallyLazyImageSlide
              url={transformations?.url ? transformations.url(image) : image}
              sources={[
                {
                  srcSet: transformations?.sources ? transformations.sources(image) : getSourceMobile(image),
                  sizes: transformations?.sizes ? transformations.sizes(image) : undefined,
                },
              ]}
              index={index}
              numberOfImages={images ? images.length : 0}
              currentIndex={currentIndex}
              fullScreen={true}
            />
          </div>
        ))}
      </ImageSliderContainer>
      <HeroGallerySliderOverlay
        initialImageIndex={imageIndex}
        scrollContainerRef={containerRef}
        amountOfSlides={images ? images.length : 0}
        slideChanged={onChange}
        scrollType={animation === 'scroll' ? 'smooth' : 'auto'}
        resetScrollType="instant"
        counterDisplay={counterDisplay}
        useKeyNavigation={true}
      />
    </div>
  ) : null;
}

type ImageSlideProps = {
  url: string;
  sources?: { srcSet: string; media?: string }[];
  loading?: 'eager' | 'lazy';
  hoverEffect?: boolean;
} & HTMLAttributes<HTMLDivElement>;

/**
 * Single Slide of the gallery. Handles scroll snapping etc.
 */
function ImageSlide(props: ImageSlideProps) {
  const { url, sources, loading = 'lazy', hoverEffect = false, ...rest } = props;

  return (
    <div
      {...rest}
      css={css`
        height: 100%;
        position: relative;
        overflow: hidden;
        ${
          hoverEffect &&
          `
        @media ${customCss.breakpoints.xl} {
          &:hover img,
          &:focus img {
            transform: scale3d(1.1, 1.1, 1);
          }
        }
        `
        }
      `}
    >
      <Picture
        alt=""
        url={url}
        sources={sources}
        loading={loading}
        className="image-slide"
        css={`
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 1s;
          transform: scale3d(1, 1, 1);
        `}
      />
    </div>
  );
}

const responsiveOptions: ResponsiveOptions = [
  {
    media: customCss.breakpoints.xxl,
    imageWidth: 1200,
    imageHeight: 800,
    bookingResolution: 'max1920x1200',
  },
  {
    media: customCss.breakpoints.xl,
    imageWidth: 1000,
    imageHeight: 1000,
    bookingResolution: 'max1280x900',
  },
  {
    media: customCss.breakpoints.sm,
    imageWidth: 552,
    imageHeight: 552,
    bookingResolution: 'max1024x768',
  },
  {
    media: customCss.breakpoints.xxs,
    imageWidth: 375,
    imageHeight: 375,
    bookingResolution: 'max500',
  },
];

export const getImageSources = (url: string) => getImageSrcSet({ url, responsiveOptions, crop: 'c_fit' });
export const getImageUrl = (url: string) =>
  getImageSrc({
    url,
    responsiveOptions,
    mediaQuery: customCss.breakpoints.xl,
    crop: 'c_fit',
  });
export const getImagePictureSizes = (url: string) => getImageSizes({ url, responsiveOptions });
